/**
 * Email Verification Modal — admin tool for the case where a client can't
 * find their verification email. Two actions:
 *  1. Resend verification email (JAD-branded, from info@jadenterprises.org)
 *  2. Override (mark verified) — bypasses the link click. Confirms first.
 *
 * Reads live verification status from Firebase Auth when a client is selected
 * so the admin sees current state before acting.
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface ClientOption {
  id: string;
  name: string;
  email: string;
}

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  clients: ClientOption[];
}

interface VerificationStatus {
  emailVerified: boolean;
  email: string;
  disabled: boolean;
}

export function EmailVerificationModal({ isOpen, onClose, clients }: EmailVerificationModalProps) {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [busy, setBusy] = useState<null | 'resend' | 'override'>(null);
  const [confirmingOverride, setConfirmingOverride] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedClientId('');
      setStatus(null);
      setResult(null);
      setConfirmingOverride(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedClientId) {
      setStatus(null);
      return;
    }
    setStatusLoading(true);
    setStatus(null);
    setResult(null);
    setConfirmingOverride(false);
    const fn = httpsCallable(functions, 'adminGetVerificationStatus');
    fn({ clientId: selectedClientId })
      .then(res => {
        const data = res.data as { success: boolean } & VerificationStatus;
        setStatus({
          emailVerified: data.emailVerified,
          email: data.email,
          disabled: data.disabled,
        });
      })
      .catch(err => {
        setResult({
          success: false,
          message: err instanceof Error ? err.message : 'Failed to load status',
        });
      })
      .finally(() => setStatusLoading(false));
  }, [selectedClientId]);

  if (!isOpen) return null;

  const selectedClient = clients.find(c => c.id === selectedClientId) || null;

  const handleClose = () => {
    setSelectedClientId('');
    setStatus(null);
    setResult(null);
    setConfirmingOverride(false);
    onClose();
  };

  const handleResend = async () => {
    if (!selectedClient) return;
    setBusy('resend');
    setResult(null);
    try {
      const fn = httpsCallable(functions, 'adminResendVerificationEmail');
      const res = await fn({ clientId: selectedClient.id });
      const data = res.data as { sent: boolean; reason?: string };
      if (data.sent) {
        setResult({ success: true, message: `Verification email resent to ${selectedClient.email}.` });
      } else {
        setResult({ success: false, message: `Not sent (${data.reason || 'unknown'}).` });
      }
    } catch (err) {
      setResult({ success: false, message: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setBusy(null);
    }
  };

  const handleOverride = async () => {
    if (!selectedClient) return;
    setBusy('override');
    setResult(null);
    try {
      const fn = httpsCallable(functions, 'adminMarkEmailVerified');
      const res = await fn({ clientId: selectedClient.id });
      const data = res.data as { changed: boolean; reason?: string };
      if (data.changed) {
        setResult({ success: true, message: `${selectedClient.email} is now marked as verified.` });
        setStatus(prev => (prev ? { ...prev, emailVerified: true } : prev));
      } else {
        setResult({ success: false, message: `No change (${data.reason || 'unknown'}).` });
      }
    } catch (err) {
      setResult({ success: false, message: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setBusy(null);
      setConfirmingOverride(false);
    }
  };

  const sortedClients = [...clients].sort((a, b) =>
    (a.name || a.email).localeCompare(b.name || b.email),
  );

  const canAct = !!status && !status.emailVerified && !busy;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={handleClose}>
      <div style={{
        background: 'var(--dark-2)', borderRadius: '12px', padding: '32px',
        maxWidth: '520px', width: '90%', border: '1px solid var(--dark-3)',
        maxHeight: '90vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>

        <h2 style={{ margin: '0 0 4px', color: 'var(--white)' }}>Email Verification</h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '20px' }}>
          Resend the verification email to a client, or override verification when the client can't locate the email and you've confirmed their identity another way.
        </p>

        <div className="form-group" style={{ marginBottom: '12px' }}>
          <label style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}>Client</label>
          <select
            value={selectedClientId}
            onChange={e => setSelectedClientId(e.target.value)}
            disabled={!!busy}
            style={{
              width: '100%', padding: '10px', background: 'var(--dark)',
              border: '1px solid var(--dark-3)', borderRadius: '6px',
              color: 'var(--white)', fontFamily: 'inherit', fontSize: '0.9rem',
            }}
          >
            <option value="">Select a client...</option>
            {sortedClients.map(c => (
              <option key={c.id} value={c.id}>
                {c.name || '(no name)'} — {c.email}
              </option>
            ))}
          </select>
        </div>

        {selectedClient && (
          <div style={{
            padding: '12px 14px', borderRadius: '6px', marginBottom: '16px',
            background: 'rgba(200,168,78,0.08)', border: '1px solid rgba(200,168,78,0.3)',
            color: 'var(--gray-200)', fontSize: '0.85rem', lineHeight: 1.6,
          }}>
            <div><strong>Email:</strong> {selectedClient.email}</div>
            <div>
              <strong>Status:</strong>{' '}
              {statusLoading ? (
                <span style={{ color: 'var(--gray-400)' }}>Checking…</span>
              ) : status ? (
                status.emailVerified ? (
                  <span style={{ color: '#10b981', fontWeight: 600 }}>✓ Verified</span>
                ) : (
                  <span style={{ color: '#f59e0b', fontWeight: 600 }}>⚠ Unverified</span>
                )
              ) : (
                <span style={{ color: 'var(--gray-400)' }}>—</span>
              )}
            </div>
          </div>
        )}

        {confirmingOverride ? (
          <div style={{
            padding: '14px 16px', borderRadius: '8px', marginBottom: '12px',
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)',
            color: '#fecaca', fontSize: '0.85rem', lineHeight: 1.55,
          }}>
            <strong style={{ color: '#fff' }}>Confirm override</strong>
            <p style={{ margin: '6px 0 12px' }}>
              You are about to mark <strong style={{ color: '#fff' }}>{selectedClient?.email}</strong> as verified without the user clicking the verification link. Only proceed if you have confirmed identity by phone or another reliable channel. This action is logged.
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleOverride}
                disabled={busy === 'override'}
                className="btn"
                style={{ flex: 1, padding: '10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600 }}
              >
                {busy === 'override' ? 'Overriding…' : 'Yes, mark as verified'}
              </button>
              <button
                onClick={() => setConfirmingOverride(false)}
                disabled={busy === 'override'}
                className="btn btn-secondary"
                style={{ flex: 1, padding: '10px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              onClick={handleResend}
              disabled={!canAct}
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: '8px', padding: '12px' }}
            >
              {busy === 'resend' ? 'Sending…' : 'Resend Verification Email'}
            </button>
            <button
              onClick={() => setConfirmingOverride(true)}
              disabled={!canAct}
              className="btn"
              style={{
                width: '100%', marginBottom: '12px', padding: '12px',
                background: 'transparent', color: '#dc2626',
                border: '1px solid rgba(239,68,68,0.5)', borderRadius: '6px', fontWeight: 600,
              }}
            >
              Override (Mark as Verified)
            </button>
          </>
        )}

        {result && (
          <div style={{
            padding: '12px 14px', borderRadius: '8px', marginBottom: '12px',
            background: result.success ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${result.success ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: result.success ? '#10b981' : '#dc2626',
            fontSize: '0.85rem', textAlign: 'center',
          }}>
            {result.message}
          </div>
        )}

        <button onClick={handleClose} className="btn btn-secondary" style={{ width: '100%' }}>
          {result?.success ? 'Done' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}
